import { useEffect, useRef, useState } from "react";
import Container from "@/components/Container";
import Pager, { usePager } from "@/components/Pager";
import { api, API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function MediaAdmin() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/media").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) { toast.error(`Upload failed: ${f.name}`); }
    }
    setUploading(false);
    toast.success(`Uploaded ${files.length} file(s)`);
    if (inputRef.current) inputRef.current.value = "";
    load();
  };

  const del = async (id) => { await api.delete(`/media/${id}`); load(); };

  const pager = usePager(items, 16);

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Library</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Media Library</h1>

      <div className="luxury-card p-6 mt-6">
        <p className="overline">Upload</p>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={upload} disabled={uploading}
          className="mt-3 block w-full text-sm text-ivory/70 file:btn-ruby file:mr-3 file:border-0"
          data-testid={ADMIN.uploadMedia} />
        <p className="text-xs text-ivory/40 mt-2">Max 12MB per file · Stored in Emergent Object Storage.</p>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.length === 0 && <p className="luxury-card p-8 text-center text-ivory/40 text-sm col-span-full">No media yet.</p>}
        {pager.pageItems.map(m => {
          const url = `${API}/files/${m.storage_path}`;
          const isImg = (m.content_type || "").startsWith("image/");
          return (
            <div key={m.id} className="luxury-card p-2" data-testid={`media-${m.id}`}>
              {isImg ? (
                <img src={url} alt={m.original_filename} className="w-full aspect-square object-cover" />
              ) : (
                <div className="aspect-square bg-charcoal-800 flex items-center justify-center text-ivory/40 text-xs uppercase tracking-[0.15em]">{m.content_type?.split("/")[1] || "file"}</div>
              )}
              <p className="text-xs text-ivory/70 mt-2 truncate">{m.original_filename}</p>
              <div className="flex justify-between items-center mt-2">
                <button onClick={() => { navigator.clipboard.writeText(url); toast.success("URL copied"); }} className="text-[0.65rem] text-gold uppercase tracking-[0.15em]">Copy URL</button>
                <button onClick={() => del(m.id)} className="text-[0.65rem] text-ruby-light uppercase tracking-[0.15em]">Delete</button>
              </div>
            </div>
          );
        })}
      </div>
    </Container>
  );
}
